import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  InputGroup,
  InputLeftElement,
  Icon,
  Card,
  CardBody,
  CardHeader,
  Center,
} from '@chakra-ui/react';
import { FiMail, FiCheckCircle, FiDownload } from 'react-icons/fi';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { PublicAPI, getTenantFallbackUUID } from '../services/api';

const ReceiveInfo = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const toast = useToast();

  // Get params from URL
  const sessionId = searchParams.get('session_id');
  const phone = searchParams.get('phone');
  const tenantId = searchParams.get('tenant_id') || getTenantFallbackUUID();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: 'Email required',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      await PublicAPI.sendDocuments({
        email,
        tenant_id: tenantId,
        session_id: sessionId,
        phone_number: phone
      });
      
      setIsSubmitted(true);
      toast({
        title: 'Request received',
        description: 'Your document request has been received for processing.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send documents. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <Container maxW="lg" py={12}>
        <Center minH="60vh" flexDirection="column">
          <Icon as={FiCheckCircle} w={20} h={20} color="green.500" mb={6} />
          <Heading as="h2" size="xl" mb={4} textAlign="center">
            Request received
          </Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center" mb={8}>
            Your request for documents at <strong>{email}</strong> has been received.
            <br />Delivery is not yet confirmed. Please check your inbox later.
          </Text>
          <Button colorScheme="blue" as={RouterLink} to="/">
            Back to home
          </Button>
        </Center>
      </Container>
    );
  }

  return (
    <Container maxW="lg" py={12} centerContent>
      <Box w="100%" mt={10}>
        <Card boxShadow="xl" borderRadius="xl" overflow="hidden">
          <Box bg="blue.600" h="10px" />
          <CardHeader textAlign="center" pb={0} pt={8}>
            <Icon as={FiDownload} w={12} h={12} color="blue.600" mb={4} />
            <Heading size="lg" mb={2}>Receive Information</Heading>
            <Text color="gray.500">
              {phone ? `Hi ${phone}, please` : 'Please'} enter your email to receive the requested company documents.
            </Text>
          </CardHeader>
          <CardBody pb={10} px={8}>
            <form onSubmit={handleSubmit}>
              <VStack spacing={5}>
                <FormControl isRequired>
                  <FormLabel>Email Address</FormLabel>
                  <InputGroup size="lg">
                    <InputLeftElement pointerEvents="none">
                      <FiMail color="gray.300" />
                    </InputLeftElement>
                    <Input 
                      type="email" 
                      placeholder="your@email.com" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoFocus
                    />
                  </InputGroup>
                </FormControl>

                <Button 
                  type="submit" 
                  colorScheme="blue" 
                  size="lg" 
                  w="100%"
                  isLoading={isLoading}
                  loadingText="Sending..."
                  mt={2}
                >
                  Send Documents Now
                </Button>
                
                <Text fontSize="xs" color="gray.400" textAlign="center" mt={4}>
                  By clicking send, you agree to receive information related to your inquiry.
                </Text>
              </VStack>
            </form>
          </CardBody>
        </Card>
        <Text textAlign="center" color="gray.400" mt={8} fontSize="sm">
          Powered by Bizsprite
        </Text>
      </Box>
    </Container>
  );
};

export default ReceiveInfo;
